import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import { groupBy, isObject, sortBy } from 'lodash';

import {
  CASE_ID_PATTERN_SOURCE,
  CASE_URL_BASE
} from '@/constants/caseId.constants';
import { ALL_URL_REGEX, AUTH0_URL, LOGSCALE_URL } from '@/constants/common';
import type { DeviceModel } from '@/features/DevicesTable';
import type { AssetDTO } from '@/pages/Assets';
import { DOUBLE_BACKSLASH_TAGS_REGEX } from '@/pages/Ticket/Ticket.constants';
import { formatUrl } from '@/pages/Ticket/Ticket.utils';
import type { GenericObject } from '@/types/common';

export const calcGroupBy = <T>(
  collection: T[],
  key: keyof T,
  limit?: number
) => {
  const groupByData = groupBy(collection, key);
  let results: {
    name: string;
    data: number[];
    count: number;
    color?: string;
  }[] = [];

  Object.keys(groupByData).forEach((name) => {
    results.push({
      name,
      data: [groupByData[name].length],
      count: groupByData[name].length
    });
  });
  results = sortBy(results, (d) => d.data[0]).reverse();
  if (limit && results.length > limit) {
    results = results.slice(0, limit);
  }

  return results;
};

export const padIp = (ip: string): string => {
  if (!ip) return '';
  return ip
    .split('.')
    .map((octet) => octet.padStart(3, '0')) // Pad each octet to 3 digits
    .join('.');
};

export const sortIpAddresses = (
  devData: DeviceModel[] | AssetDTO[]
): DeviceModel[] | AssetDTO[] => {
  return devData.sort((a, b) => {
    const paddedA = padIp(a?.ip_address || '');
    const paddedB = padIp(b?.ip_address || '');
    return paddedA.localeCompare(paddedB);
  });
};

export const sortMixedIpString = (a: string, b: string) => {
  const valA = /^\d{1,3}(\.\d{1,3}){3}$/.test(a) ? padIp(a) : a.toLowerCase();
  const valB = /^\d{1,3}(\.\d{1,3}){3}$/.test(b) ? padIp(b) : b.toLowerCase();

  return valA.localeCompare(valB);
};

export const sortString = (a: string, b: string) => {
  const stringA = a?.toLowerCase() ?? '';
  const stringB = b?.toLowerCase() ?? '';
  return stringA.localeCompare(stringB);
};

// flatten an object to a single level
// input: {a: 1, d: {dd: 'Y'}}
// output: {a: 1, d_dd: 'Y'}
export const flattenObj = (
  obj: GenericObject,
  roots = [],
  sep = '_'
): Record<string, string> =>
  Object.keys(obj).reduce((memo, prop) => {
    Object.assign(
      memo,
      isObject(obj[prop])
        ? flattenObj(
            obj[prop] as GenericObject,
            roots.concat([prop as never]),
            sep
          )
        : { [roots.concat([prop as never]).join(sep)]: obj[prop] }
    );
    return memo;
  }, {});

export const getUserInitials = (name: string) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('');
};

export const stringToColor = (string: string) => {
  let hash = 0;
  let i: number;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
};

export const parse_and_replace_links = (inputText: string) => {
  if (typeof inputText !== 'string') return inputText;

  let replacedText: string;

  // URLs starting with http://, https://, or ftp://
  const replacePattern1 =
    /(\b(https?|ftp):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gim;
  replacedText = inputText.replace(
    replacePattern1,
    '<a href="$1" target="_blank">$1</a>'
  );

  // Change email addresses to mailto:: links.
  const replacePattern3 =
    /(([a-zA-Z0-9\-_.])+@[a-zA-Z_]+?(\.[a-zA-Z]{2,6})+)/gim;
  replacedText = replacedText.replace(
    replacePattern3,
    '<a href="mailto:$1">$1</a>'
  );

  return replacedText;
};

// convert logscale links to use SSO
export const convertLogscaleLinks = (inputText: string) => {
  const client_id = 'rGMhnzpSjqllO6j6b86MNrgrlz2noGNk';
  let encoded_text = inputText;
  try {
    encoded_text = btoa(inputText);
  } catch (e) {
    console.error('Error encoding text:', e);
  }
  const redirect_uri = `${LOGSCALE_URL}/auth/auth0`;
  const scope = 'openid%20profile%20email';
  const url = `${AUTH0_URL}/authorize?client_id=${client_id}&response_type=code&redirect_uri=${redirect_uri}&scope=${scope}&state=${encoded_text}`;

  return url;
};

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes or a string like "122313123" or "1213123133kb".
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export const humanizeFileSize = (
  bytesValue: number | string = 0,
  si = false,
  dp = 1
) => {
  const thresh = si ? 1000 : 1024;
  let bytes = bytesValue;
  if (!bytes || bytes === '0') {
    return '0 B';
  }

  if (typeof bytes === 'string' && bytes.toLocaleLowerCase().includes('kb')) {
    bytes = parseInt(bytes, 10) * thresh;
  } else if (typeof bytes === 'string') {
    bytes = parseInt(bytes, 10);
  }

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`;
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (
    Math.round(Math.abs(bytes) * r) / r >= thresh &&
    u < units.length - 1
  );

  return `${bytes.toFixed(dp)} ${units[u]}`;
};

// converts string to array of strings
export const parseIpInput = (input: string | undefined) => {
  if (!input) return [];
  const stringArray = input
    .replace(/ /g, '')
    .replace(/,/g, '\n')
    .split('\n')
    .filter(Boolean);
  return stringArray;
};

export const safeJsonParse = (
  value: string | null,
  defaultValue: unknown = {}
) => {
  try {
    return value ? JSON.parse(value) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export default calcGroupBy;

export const getFileExtension = (filename: string) => {
  return `.${filename.split('.').pop()?.toLowerCase() || ''}`;
};

export function formatLargeNumber(num: number, longFormat = false): string {
  const format = (value: number) => {
    const rounded = Number(value.toFixed(2));
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  };

  if (num >= 1_000_000_000_000) {
    const value = num / 1_000_000_000_000;
    return `${format(value)}${longFormat ? ' Trillion' : 'T'}`;
  }
  if (num >= 1_000_000_000) {
    const value = num / 1_000_000_000;
    return `${format(value)}${longFormat ? ' Billion' : 'B'}`;
  }
  if (num >= 1_000_000) {
    const value = num / 1_000_000;
    return `${format(value)}${longFormat ? ' Million' : 'M'}`;
  }
  if (num >= 1_000) {
    const value = num / 1_000;
    return `${format(value)}${longFormat ? ' Thousand' : 'K'}`;
  }

  return `${num}`;
}

export const getCountryName = (countryCode: string): string => {
  try {
    const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return displayNames.of(countryCode.toUpperCase()) || countryCode;
  } catch {
    return countryCode;
  }
};

export const hasDataGreaterThanZero = (
  dataArr?: AgBarSeriesOptions['data']
): boolean => {
  if (!dataArr?.length) return false;
  return dataArr.some((item) =>
    Object.entries(item).some(
      ([key, subItem]) =>
        key !== 'date' && typeof subItem === 'number' && subItem > 0
    )
  );
};

export const resolveCssVariable = (
  variableName: string,
  element = document.documentElement
) => {
  // if the input looks like `var(--foo)`, extract the name
  const match = variableName.match(/^var\((--[^)]+)\)$/);
  const cssVar = match ? match[1] : variableName;

  // get the computed value from the given element (default: :root)
  const value = getComputedStyle(element).getPropertyValue(cssVar).trim();

  return value || null;
};

export const toTitleCase = (str: string) => {
  return str
    .split(' ')
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const hasMarkdownSyntax = (text: string): boolean => {
  // Check for common markdown patterns
  const markdownPatterns = [
    /\*\*.*?\*\*/, // Bold: **text**
    /\*.*?\*/, // Italic: *text*
    /_.*?_/, // Italic: _text_
    /\n/, // Newlines
    /^#{1,6}\s/m, // Headers: # text
    /\[.*?\]\(.*?\)/, // Links: [text](url)
    /`.*?`/, // Inline code: `code`
    /^[-*+]\s/m, // Unordered lists
    /^\d+\.\s/m // Ordered lists
  ];

  return markdownPatterns.some((pattern) => pattern.test(text));
};

// convert turbine ids to use links
export const convertTurbineLinks = (turbineId: string): string => {
  const turbineAccountId = '0196ace5-8695-79ba-9ae4-b147310d37f1';
  const turbineTenantId = '0196f4b9-cc75-7e0f-bcc9-54ad328b68bc';
  const turbineAppId = 'aGsC5uP_JgBut3IYs';
  if (!turbineAccountId || !turbineTenantId || !turbineAppId) {
    return '';
  }
  const encodedTurbineId = encodeURIComponent(turbineId);
  const url = `https://usn.swimlane.app/account/${turbineAccountId}/tenant/${turbineTenantId}/record/${turbineAppId}/${encodedTurbineId}`;
  return url;
};

export const getSessionStorageData = (storageKey: string) => {
  try {
    return JSON.parse(sessionStorage.getItem(storageKey) || 'null');
  } catch (_error) {
    return null;
  }
};

/**
 * Markdown component escapes markdown characters like `*` or `_` in URLs. If that is saved 1:1 into the DB it breaks URLs
 * This Method unescapes those characters prior to saving into the DB, so that they can be properly rendered in the UI.
 * @param content - string of the markdown content to be sanitized
 * @returns - sanitized markdown content
 */
export const sanitizeMarkdownContent = (content: string) => {
  const formattedContentUrls = content.replace(ALL_URL_REGEX, (match) =>
    formatUrl('remove', match)
  );

  // to display tags properly in the ui, we need to remove double backslashes prior to saving
  const formattedContent = formattedContentUrls.replace(
    DOUBLE_BACKSLASH_TAGS_REGEX,
    (match) => match.substring(1)
  );

  return formattedContent;
};

/**
 * Parses a string and replaces all case IDs (words starting with "CIM-") with links to the case details page
 * @param text - The text to parse
 * @returns An array of React elements (text strings and Link components)
 */
export const parseCaseIdsToLinks = (text: string): string[] => {
  if (!text) return [text];

  const caseIdPattern = new RegExp(`\\b(${CASE_ID_PATTERN_SOURCE})\\b`, 'g');

  const parts: string[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Find all matches in the text
  while ((match = caseIdPattern.exec(text)) !== null) {
    const caseId = match[1];
    const matchIndex = match.index;

    // Add text before the match
    if (matchIndex > lastIndex) {
      parts.push(text.substring(lastIndex, matchIndex));
    }

    parts.push(`${CASE_URL_BASE}${caseId}`);

    lastIndex = matchIndex + caseId.length;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  // If no matches were found, return the original text
  return parts.length > 0 ? parts : [text];
};

/**
 * Extracts a case ID (e.g. "CIM-1234") from a case-detail URL, ignoring any
 * trailing slash, query string, or hash fragment.
 * @param url - A URL whose path ends in the case ID (e.g. `.../case/CIM-1234?tab=notes`)
 * @returns The case ID, or undefined if the URL's last path segment isn't a valid case ID
 */
export const extractCaseId = (url: string): string | undefined => {
  let pathname: string;

  try {
    pathname = new URL(url).pathname;
  } catch {
    // Not an absolute URL (e.g. a relative path) - fall back to stripping
    // the query/hash manually before pulling the last path segment.
    pathname = url.split(/[?#]/)[0];
  }

  const caseId = pathname.replace(/\/+$/, '').split('/').pop()?.trim();
  const caseIdValidator = new RegExp(`^${CASE_ID_PATTERN_SOURCE}$`, 'i');

  return caseId && caseIdValidator.test(caseId) ? caseId : undefined;
};

const LINKIFY_SKIP_TAGS = new Set(['A', 'CODE', 'PRE', 'SCRIPT', 'STYLE']);

const hasSkippedAncestor = (node: Node, root: Node): boolean => {
  let current = node.parentNode;

  while (current && current !== root) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      LINKIFY_SKIP_TAGS.has((current as Element).tagName)
    ) {
      return true;
    }
    current = current.parentNode;
  }

  return false;
};

/**
 * Rewrites bare "CIM-1234" mentions found in text nodes of an HTML string into
 * anchor tags, without touching markup inside existing links, code, or script/style
 * blocks. Used for message bodies that contain raw HTML (rendered via
 * dangerouslySetInnerHTML), where markdown's AST-based linking doesn't apply.
 * @param html - Raw HTML string to scan
 * @param caseBasePath - App-relative path prefix for case links, e.g. "/case/" or "/case-internal/"
 * @returns The HTML string with case IDs replaced by `<a>` tags
 */
export const linkifyCaseIdsInHtml = (
  html: string,
  caseBasePath: string
): string => {
  if (!html || typeof document === 'undefined') return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  const pattern = new RegExp(`\\b${CASE_ID_PATTERN_SOURCE}\\b`, 'g');
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  let node = walker.nextNode();

  while (node) {
    const textNode = node as Text;
    pattern.lastIndex = 0;

    if (
      !hasSkippedAncestor(textNode, container) &&
      pattern.test(textNode.data)
    ) {
      textNodes.push(textNode);
    }

    node = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const fragment = document.createDocumentFragment();
    const { data } = textNode;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    pattern.lastIndex = 0;

    while ((match = pattern.exec(data)) !== null) {
      const caseId = match[0];

      if (match.index > lastIndex) {
        fragment.appendChild(
          document.createTextNode(data.slice(lastIndex, match.index))
        );
      }

      const anchor = document.createElement('a');
      anchor.href = `${caseBasePath}${caseId}`;
      anchor.textContent = caseId;
      fragment.appendChild(anchor);
      lastIndex = match.index + caseId.length;
    }

    if (lastIndex < data.length) {
      fragment.appendChild(document.createTextNode(data.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  });

  return container.innerHTML;
};
